import {
  initializeApp,
  getApps,
  getApp,
  type FirebaseApp,
  type FirebaseOptions,
} from 'firebase/app';
import {
  browserLocalPersistence,
  connectAuthEmulator,
  browserPopupRedirectResolver,
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  indexedDBLocalPersistence,
  initializeAuth,
  OAuthProvider,
  signInAnonymously,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
  type Auth,
  type User,
} from 'firebase/auth';
import {
  connectFirestoreEmulator,
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions';
import { getMessaging, type Messaging } from 'firebase/messaging';
import { connectStorageEmulator, getStorage, type FirebaseStorage } from 'firebase/storage';
import { getI18n } from 'react-i18next';

import { signOutWithMediaCheck } from '../lib/signOutFlow';

const cfg: FirebaseOptions = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let cached:
  | {
      app: FirebaseApp;
      auth: Auth;
      firestore: Firestore;
      storage: FirebaseStorage;
      functions: Functions;
    }
  | undefined;

/**
 * Make the auth session persistence explicit (local — survives reload/restart)
 * instead of relying on the SDK default. Prefers IndexedDB, falls back to
 * localStorage. `initializeAuth` can only run once per app, so reuse the
 * existing instance under Vite HMR / double-init.
 *
 * `popupRedirectResolver` MUST be passed explicitly: unlike `getAuth()`,
 * `initializeAuth()` does NOT auto-register it, and without it
 * `signInWithPopup` (Google / Apple) throws `auth/argument-error`.
 */
function resolveAuth(app: FirebaseApp): Auth {
  try {
    return initializeAuth(app, {
      persistence: [indexedDBLocalPersistence, browserLocalPersistence],
      popupRedirectResolver: browserPopupRedirectResolver,
    });
  } catch {
    return getAuth(app);
  }
}

/**
 * Force long-polling for the Firestore transport. Safari (ITP/content-blocking)
 * and some corporate proxies/VPNs reject Firestore's default streaming
 * WebChannel — the `Listen/channel … due to access control checks` error — which
 * silently kills every realtime `onSnapshot` (delivered-work, timesheets, etc.).
 * Long-polling uses ordinary requests that those environments allow. Auto-detect
 * is on by default in v11 but still probes the blocked stream first; forcing it
 * skips that probe entirely. `initializeFirestore` runs once per app, so fall
 * back to `getFirestore` under HMR / double-init.
 *
 * PWA-2 — `persistentLocalCache` adds Firestore's own IndexedDB offline layer:
 * previously-read docs stay readable with no network and writes queue locally,
 * replaying on reconnect. `persistentMultipleTabManager` is required because
 * admins routinely keep several tabs open; the single-tab default would throw
 * `failed-precondition` in every tab but the first. This is SEPARATE from the
 * deeper field sync queue (PWA-4) — Firestore's queue does not survive photo
 * uploads or a hard app kill mid-write.
 *
 * NOTE: the cache is per-origin, not per-user — data a user already loaded stays
 * in IndexedDB after sign-out. Acceptable on personal devices (it is the point of
 * offline); see the PWA-2 note in docs/PROGRESS.md for the shared-desktop caveat.
 */
function resolveFirestore(app: FirebaseApp): Firestore {
  try {
    return initializeFirestore(app, {
      experimentalForceLongPolling: true,
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
    });
  } catch {
    return getFirestore(app);
  }
}

/**
 * Point the whole SDK at the local emulators, for the browser acceptance
 * suite.
 *
 * Those flows have to run against the code in this working tree, and until
 * now the only way to open them in a browser was to deploy — so a change
 * could not be accepted in a browser before it shipped, which is the wrong
 * way round.
 *
 * TWO locks, because a bundle that talks to an emulator is a bundle that
 * trusts an unauthenticated database:
 *
 *   • the build flag `VITE_FIREBASE_EMULATORS`, which the deploy wrapper
 *     never sets, and
 *   • the HOST at runtime. A production bundle built with the flag on by
 *     accident still refuses, because it is not being served from a
 *     loopback address.
 *
 * Both must hold. Neither alone is enough: the flag is a build-time
 * intention and the host is what is actually true when the page runs.
 */
const LOOPBACK = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);

function emulatorHost(): string | null {
  if (import.meta.env.VITE_FIREBASE_EMULATORS !== 'true') return null;
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname;
  if (!LOOPBACK.has(host)) {
    // Loud, not silent: a bundle built for the emulators and served from a
    // real origin is a misconfigured deploy, and it must be obvious.
    console.error('VITE_FIREBASE_EMULATORS is set but the app is not on localhost — ignoring.');
    return null;
  }
  return host === '[::1]' || host === '::1' ? '127.0.0.1' : host;
}

function connectEmulators(parts: {
  auth: Auth;
  firestore: Firestore;
  storage: FirebaseStorage;
  functions: Functions;
}): void {
  const host = emulatorHost();
  if (!host) return;
  connectAuthEmulator(parts.auth, `http://${host}:9099`, { disableWarnings: true });
  connectFirestoreEmulator(parts.firestore, host, 8080);
  connectFunctionsEmulator(parts.functions, host, 5001);
  connectStorageEmulator(parts.storage, host, 9199);
}

export function initFirebase() {
  if (cached) return cached;
  const app = getApps().length === 0 ? initializeApp(cfg) : getApp();
  cached = {
    app,
    auth: resolveAuth(app),
    firestore: resolveFirestore(app),
    storage: getStorage(app),
    functions: getFunctions(app, 'europe-west1'),
  };
  connectEmulators(cached);
  return cached;
}

export function getFirebase() {
  if (!cached) throw new Error('Firebase not initialized. Call initFirebase() at app bootstrap.');
  return cached;
}

/**
 * EPIC 47 P2a — lazily resolve FCM messaging. Not part of the eager `initFirebase`
 * bundle because messaging is only needed once a user opts into push, and
 * `getMessaging` throws in environments without a service worker (e.g. some
 * in-app browsers, SSR). Returns null instead of throwing so callers degrade
 * gracefully.
 */
export function getMessagingSafe(): Messaging | null {
  try {
    const { app } = getFirebase();
    return getMessaging(app);
  } catch {
    return null;
  }
}

export async function signInWithEmail(email: string, password: string): Promise<User> {
  const { auth } = getFirebase();
  const { user } = await signInWithEmailAndPassword(auth, email, password);
  return user;
}

/**
 * Ensure there's a signed-in user for B2C guest checkout. If nobody is signed
 * in, sign in anonymously so guests can place orders without an account while
 * the order endpoint stays authenticated. No-op if already signed in.
 */
export async function ensureAnonymousAuth(): Promise<User> {
  const { auth } = getFirebase();
  if (auth.currentUser) return auth.currentUser;
  const { user } = await signInAnonymously(auth);
  return user;
}

export async function signInWithGoogle(): Promise<User> {
  const { auth } = getFirebase();
  const provider = new GoogleAuthProvider();
  const { user } = await signInWithPopup(auth, provider);
  return user;
}

export async function signInWithApple(): Promise<User> {
  const { auth } = getFirebase();
  const provider = new OAuthProvider('apple.com');
  provider.addScope('email');
  provider.addScope('name');
  const { user } = await signInWithPopup(auth, provider);
  return user;
}

export async function signOutCurrent(): Promise<void> {
  const { purgeQueueMediaForSignOut, hasUnsentQueueMedia } = await import('./syncQueue');
  return signOutWithMediaCheck({
    hasMedia: hasUnsentQueueMedia,
    confirm: (unavailable) =>
      window.confirm(
        getI18n().t(unavailable ? 'common:queueLogout.unavailable' : 'common:queueLogout.confirm'),
      ),
    purge: purgeQueueMediaForSignOut,
    warn: () => window.alert(getI18n().t('common:queueLogout.cleanupFailed')),
    finish: finishSignOut,
  });
}

async function finishSignOut(): Promise<void> {
  // Best-effort: drop this device's push token before signing out so a shared
  // browser doesn't keep receiving the previous user's pushes. Dynamic import
  // avoids a static cycle (push.ts imports firebase.ts). Never blocks logout.
  try {
    const { disablePush } = await import('./push');
    await disablePush();
  } catch {
    /* swallow — logout must proceed regardless */
  }
  const { auth } = getFirebase();
  // Leaving on purpose is not the same as being removed: clear the marker
  // first, so the login page does not tell somebody who pressed "Log out"
  // that their session "ended".
  try {
    localStorage.removeItem('rm24.wasSignedIn');
  } catch {
    // A browser with storage blocked simply gets no explanation.
  }
  await signOut(auth);
}

export async function signUpWithEmail(
  email: string,
  password: string,
  displayName: string,
): Promise<User> {
  const { auth } = getFirebase();
  const { user } = await createUserWithEmailAndPassword(auth, email, password);
  if (displayName) {
    await updateProfile(user, { displayName });
  }
  return user;
}
